import React, { useEffect, useMemo, useState } from "react";
import Plot from "react-plotly.js";
import { loadTBFigure } from "../../services/tbOutbreakApi";

export default function TBPlotlyPanel({
  filename,
  title,
  scopeLabel,
  description,
}) {
  const [figure, setFigure] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    setFigure(null);
    setError("");

    loadTBFigure(filename)
      .then((data) => {
        if (active) {
          setFigure(data);
        }
      })
      .catch((err) => {
        console.error(`Failed to load TB figure ${filename}:`, err);

        if (active) {
          setError(err.message);
        }
      });

    return () => {
      active = false;
    };
  }, [filename]);

  const layout = useMemo(() => {
    if (!figure) {
      return null;
    }

    const sourceLayout = figure.layout || {};

    const axisOverrides = {};

    Object.keys(sourceLayout).forEach((key) => {
      if (/^[xy]axis\d*$/.test(key)) {
        axisOverrides[key] = {
          ...sourceLayout[key],
          automargin: true,
        };
      }
    });

    return {
      ...sourceLayout,

      // Charts remain visually independent from the blue ESCORT page.
      paper_bgcolor: "#ffffff",
      plot_bgcolor: "#ffffff",

      font: {
        ...(sourceLayout.font || {}),
        color: "#48545e",
      },

      // The panel already has its own title.
      title: sourceLayout.title
        ? {
            ...sourceLayout.title,
            text: "",
          }
        : undefined,

      legend: {
        ...(sourceLayout.legend || {}),
        orientation: "h",
        yanchor: "bottom",
        y: 1.02,
        xanchor: "left",
        x: 0,
      },

      margin: {
        ...(sourceLayout.margin || {}),
        t: 50,
        r: 20,
        b: sourceLayout.margin?.b ?? 55,
        l: sourceLayout.margin?.l ?? 60,
      },

      autosize: true,

      ...axisOverrides,
    };
  }, [figure]);

  return (
    <div className="tb-chart-panel">
      <div className="tb-chart-panel-header">
        <div>
          {scopeLabel && (
            <div className="tb-chart-scope">
              {scopeLabel}
            </div>
          )}

          <h3>{title}</h3>

          {description && (
            <p>{description}</p>
          )}
        </div>
      </div>

      <div className="tb-chart-content">
        {error && (
          <div className="tb-chart-error">
            <strong>Could not load this chart.</strong>
            <span>{error}</span>
          </div>
        )}

        {!figure && !error && (
          <div className="tb-chart-loading">
            Loading chart...
          </div>
        )}

        {figure && layout && (
          <Plot
            data={figure.data}
            layout={layout}
            config={{
              responsive: true,
              displayModeBar: false,
            }}
            useResizeHandler
            style={{
              width: "100%",
              height: `${figure.layout?.height ?? 460}px`,
            }}
          />
        )}
      </div>
    </div>
  );
}